// The between-seasons check-in — two taps, once a month.
//
// This is the parent's only steering wheel over what their child's stories are
// about, so it is deliberately the smallest possible surface: one 5-face progress
// question and one pick from a list we already know. Two design constraints drive
// every choice here:
//   • It must be answerable in seconds. Microsurvey practice is 2-3 questions and
//     "under 5 seconds"; anything that needs typing loses most respondents.
//   • Autonomy has to be STRUCTURED. Fully open goal-setting overwhelms most
//     people, so the options are the child's own focus queue with a recommended
//     default already selected — never a blank field.
// And «بعداً» is a first-class answer: an unanswered check-in auto-resolves to its
// default server-side, so skipping costs the parent nothing.
import { useState } from "react";
import { X } from "lucide-react";
import { useLang } from "../../i18n.jsx";
import { api } from "../api.js";
import { FluentEmoji } from "../emoji.jsx";
import { haptic } from "../host.js";
import { C, FONT, alpha } from "../../design-system.js";

// A 5-point face scale mapped onto the 0-10 goal-attainment scale used for
// parent-reported progress. Five points, not eleven: a slider asks for a precision
// no parent actually has, and the thresholds we act on are only three wide.
const FACES = [
  { emoji: "😟", value: 0,  label: "هیچ تغییری ندیدم" },
  { emoji: "🙁", value: 3,  label: "خیلی کم" },
  { emoji: "🙂", value: 5,  label: "کمی بهتر شده" },
  { emoji: "😊", value: 8,  label: "پیشرفت خوبی داشته" },
  { emoji: "🤩", value: 10, label: "خیلی بهتر شده" },
];

export function SeasonCheckinModal({ checkin, onClose, onDone }) {
  const { t } = useLang();
  const [progress, setProgress] = useState(null);
  const [choice, setChoice] = useState(checkin?.recommended || null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  if (!checkin) return null;
  const options = checkin.options || [];

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await api(`/seasons/checkin/${checkin.id}`, {
        method: "POST",
        body: JSON.stringify({ progress, next_focus: choice }),
      });
      haptic("success");
      setResult(res);
    } catch (e) {
      setError(e.message || t("خطا در ثبت پاسخ"));
      haptic("error");
    } finally {
      setBusy(false);
    }
  }

  const shell = {
    position: "fixed", inset: 0, background: alpha(C.bgDeep, 0.82), zIndex: 200,
    display: "flex", alignItems: "flex-end", justifyContent: "center",
    fontFamily: FONT, padding: "0 0 env(safe-area-inset-bottom)",
  };
  const sheet = {
    width: "100%", maxWidth: 430, background: C.bgCard,
    border: `1px solid ${C.borderGold}`, borderRadius: "24px 24px 0 0",
    padding: "22px 20px calc(22px + var(--tg-safe-bottom, 0px))",
    maxHeight: "88dvh", overflowY: "auto", animation: "slideUp 0.3s ease",
  };

  // ── Done ────────────────────────────────────────────────────────────────────
  if (result) {
    return (
      <div style={shell} onClick={onDone}>
        <div style={sheet} onClick={e => e.stopPropagation()}>
          <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
            <FluentEmoji anim="bounce" size={62} style={{ marginBottom: 12 }}>🌱</FluentEmoji>
            <h3 style={{ color: C.cream, fontSize: 20, fontWeight: 800, margin: "0 0 10px" }}>
              {t("ثبت شد، ممنون!")}
            </h3>
            <p style={{ color: C.textMid, fontSize: 15, lineHeight: 1.9, margin: "0 0 18px" }}>
              {result.next_focus
                ? t("قصه‌های ماه بعدِ {name} حول «{focus}» ساخته می‌شوند.", { name: checkin.child_name, focus: result.next_focus })
                : t("قصه‌های ماه بعد بر اساس پاسخ شما تنظیم شد.")}
            </p>
            {/* The gentle advisory — shown ONLY when a full dose moved nothing.
                Deliberately non-diagnostic: it names no condition, and it never
                suggests more of the same stories, because eight targeted stories
                that changed nothing is not an argument for a ninth. */}
            {result.flagged && (
              <div style={{ background: `${C.purple}14`, border: `1px solid ${C.purple}33`, borderRadius: 14, padding: "14px 16px", textAlign: "start", marginBottom: 18 }}>
                <div style={{ color: C.purple, fontSize: 14.5, fontWeight: 700, marginBottom: 6 }}>
                  {t("یک پیشنهاد دوستانه")}
                </div>
                <div style={{ color: C.creamDim, fontSize: 14, lineHeight: 1.95 }}>
                  {t("یک دورهٔ کامل قصه روی این موضوع کار کرد و تغییر محسوسی ندیدید. این نه به‌معنای مشکل جدی است و نه تشخیص — فقط نشانهٔ آن است که شاید گفت‌وگو با یک مشاور کودک کمکِ سریع‌تری باشد. ما از ماه بعد روی موضوع دیگری تمرکز می‌کنیم.")}
                </div>
              </div>
            )}
            <button onClick={onDone} className="press"
              style={{ width: "100%", background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, color: C.bgDeep, border: "none", borderRadius: 16, padding: "15px 0", fontSize: 17, fontWeight: 800, cursor: "pointer", fontFamily: FONT }}>
              {t("باشه")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Ask ─────────────────────────────────────────────────────────────────────
  return (
    <div style={shell} onClick={onClose}>
      <div style={sheet} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ color: C.cream, fontSize: 19, fontWeight: 800, margin: "0 0 6px" }}>
              {t("یک پرسش کوتاه دربارهٔ {name}", { name: checkin.child_name })}
            </h3>
            <p style={{ color: C.textMid, fontSize: 13.5, lineHeight: 1.8, margin: 0 }}>
              {t("چند ثانیه وقت می‌برد و قصه‌های ماه بعد را دقیق‌تر می‌کند.")}
            </p>
          </div>
          <button onClick={onClose} aria-label={t("بستن")}
            style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", padding: 4, display: "flex" }}>
            <X size={20} />
          </button>
        </div>

        {/* Q1 — progress on the season's focus */}
        {checkin.focus && (
          <div style={{ marginBottom: 22 }}>
            <div style={{ color: C.cream, fontSize: 15.5, fontWeight: 700, marginBottom: 4 }}>
              {t("قصه‌های این ماه روی «{focus}» کار کردند.", { focus: checkin.focus })}
            </div>
            <div style={{ color: C.textMid, fontSize: 14, marginBottom: 12 }}>
              {t("چقدر تغییر دیده‌اید؟")}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
              {FACES.map(f => {
                const on = progress === f.value;
                return (
                  <button key={f.value} onClick={() => { haptic("selection"); setProgress(f.value); }}
                    className="press" title={t(f.label)}
                    style={{
                      flex: 1, background: on ? `${C.gold}1F` : C.bg,
                      border: `1.5px solid ${on ? C.gold : C.border}`,
                      borderRadius: 14, padding: "10px 0", cursor: "pointer", fontFamily: FONT,
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                    }}>
                    <FluentEmoji size={28}>{f.emoji}</FluentEmoji>
                  </button>
                );
              })}
            </div>
            {progress != null && (
              <div style={{ color: C.gold, fontSize: 13, textAlign: "center", marginTop: 8 }}>
                {t(FACES.find(f => f.value === progress).label)}
              </div>
            )}
          </div>
        )}

        {/* Q2 — what next month should work on */}
        {options.length > 1 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ color: C.cream, fontSize: 15.5, fontWeight: 700, marginBottom: 10 }}>
              {t("ماه بعد روی چه چیزی تمرکز کنیم؟")}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {options.map(o => {
                const on = choice === o.label;
                return (
                  <button key={o.label} onClick={() => { haptic("selection"); setChoice(o.label); }}
                    className="press"
                    style={{
                      width: "100%", textAlign: "start", background: on ? `${C.gold}14` : C.bg,
                      border: `1.5px solid ${on ? C.gold : C.border}`, borderRadius: 14,
                      padding: "13px 15px", cursor: "pointer", fontFamily: FONT,
                      color: on ? C.cream : C.creamDim, fontSize: 15, fontWeight: on ? 700 : 500,
                      display: "flex", alignItems: "center", gap: 9,
                    }}>
                    <span style={{ flex: 1 }}>{o.label}</span>
                    {o.current && (
                      <span style={{ background: `${C.teal}22`, color: C.teal, borderRadius: 999, padding: "3px 9px", fontSize: 11.5, fontWeight: 700 }}>
                        {t("همین حالا")}
                      </span>
                    )}
                    {o.done && !o.current && (
                      <span style={{ background: `${C.textDim}22`, color: C.textMid, borderRadius: 999, padding: "3px 9px", fontSize: 11.5, fontWeight: 700 }}>
                        {t("کار شده")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {error && <div style={{ color: C.red, fontSize: 13.5, marginBottom: 12, textAlign: "center" }}>{error}</div>}

        <button onClick={submit} disabled={busy} className="press celebrate"
          style={{ width: "100%", background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, color: C.bgDeep, border: "none", borderRadius: 16, padding: "15px 0", fontSize: 17, fontWeight: 800, cursor: busy ? "default" : "pointer", fontFamily: FONT, opacity: busy ? 0.6 : 1 }}>
          {busy ? t("در حال ثبت...") : t("ثبت پاسخ")}
        </button>
        <button onClick={onClose}
          style={{ width: "100%", background: "none", border: "none", color: C.textMid, fontSize: 14.5, fontWeight: 600, cursor: "pointer", fontFamily: FONT, padding: "13px 0 0" }}>
          {t("بعداً — قصه‌ها به‌هرحال ادامه دارند")}
        </button>
      </div>
    </div>
  );
}
